<template>
  <button
    class="base-button"
    :class="computedClass"
    :type="props.type"
    :disabled="props.disabled"
    :style="computedStyle"
    @click="handleClick"
    @pointerdown="handlePointerDown"
  >
    <div
      v-show="props.withDecor"
      class="base-button__circle base-button__circle--top-left"
    />
    <div
      v-show="props.withDecor"
      class="base-button__circle base-button__circle--bottom-left"
    />
    <div
      v-if="
        !props.navigated && props.withFull && !props.withIconDense
          ? $slots['prepend-icon'] || $slots['append-icon']
          : $slots['prepend-icon']
      "
      class="base-button__icon base-button__icon--prepend"
    >
      <slot name="prepend-icon" />
    </div>
    <p :style="props.textColor ? { color: props.textColor } : {}">
      <slot />
    </p>
    <div v-if="props.loading" class="base-button__loading">
      <BaseLoader
        size="28px"
        :color="colorLoader"
      />
    </div>
    <div
      v-if="
        props.withFull && !props.withIconDense
          ? $slots['prepend-icon'] || $slots['append-icon']
          : $slots['append-icon']
      "
      class="base-button__icon base-button__icon--append"
    >
      <slot name="append-icon" />
    </div>
    <div
      v-show="props.withDecor && !props.navigated"
      class="base-button__circle base-button__circle--top-right"
    />
    <div
      v-show="props.withDecor && !props.navigated"
      class="base-button__circle base-button__circle--bottom-right"
    />
  </button>
</template>

<script setup lang="ts">
import { playActionEffectTap } from '~/shared/lib'
import BaseLoader from '../loader/index.vue'

export interface IProps {
  text?: string
  variant?:
    | 'dark'
    | 'light'
    | 'primary'
    | 'secondary'
    | 'danger'
    | 'success'
    | 'transparent'
    | 'transparent-inverted-100'
  color?: string
  size?: 'mini' | 'sm' | 'md' | 'md-alt' | 'lg'
  type?: 'submit' | 'button'
  textColor?: string
  height?: string
  maxWidth?: string
  outlined?: boolean
  flat?: boolean
  navigated?: boolean
  rounded?: boolean
  shadow?: boolean
  loading?: boolean
  disabled?: boolean
  withFull?: boolean
  withDecor?: boolean
  withIconDense?: boolean
  withMobile?: boolean
}

const props = withDefaults(defineProps<IProps>(), {
  variant: 'dark',
  size: 'md',
  type: 'button',
  withFull: false,
  withDecor: false,
  textAlign: 'center',
  withIconDense: false,
})

const emit = defineEmits<{
  (e: 'click', value: MouseEvent): void
}>()

const computedClass = computed(() => ({
  [`base-button--variant-${props.variant}`]: true,
  [`base-button--size-${props.size}`]: true,
  'base-button--outlined': props.outlined,
  'base-button--flat': props.flat,
  'base-button--rounded': props.rounded,
  'base-button--shadow': props.shadow,
  'base-button--navigated': props.navigated,
  'base-button--with-full': props.withFull,
  'base-button--with-decor': props.withDecor,
  'base-button--with-loading': props.loading,
  'base-button--with-icon-dense': props.withIconDense,
  'base-button--with-mobile': props.withMobile,
  'base-button--with-custom-color': props.color,
}))

const computedStyle = computed(() => ({
  ...(props.height ? { '--button-height': props.height } : {}),
  ...(props.maxWidth ? { '--button-max-width': props.maxWidth } : {}),
  ...(props.color ? { '--button-custom-color': props.color } : {}),
}))

const colorLoader = computed(() => {
  if (props.flat) {
    return 'var(--color-foreground-primary)'
  }

  switch (props.variant) {
    case 'dark':
      return 'var(--color-foreground-inverted)'
    case 'light':
      return 'var(--color-foreground-main)'
    case 'primary':
      return 'var(--color-foreground-light)'
    case 'secondary':
      return 'var(--color-foreground-regular)'
    case 'danger':
      return 'var(--color-foreground-light)'
    case 'success':
      return 'var(--color-foreground-light)'
    case 'transparent':
    case 'transparent-inverted-100':
      return 'var(--color-foreground-inverted)'
    default:
      return ''
  }
})

function handlePointerDown(event: PointerEvent) {
  if (props.loading || props.disabled) {
    return
  }

  playActionEffectTap(event.currentTarget as HTMLButtonElement)
}

function handleClick(event: MouseEvent) {
  if (props.loading || props.disabled) {
    return
  }

  emit('click', event)
  ;(event.currentTarget as HTMLButtonElement).blur()
}
</script>

<style scoped lang="scss">
.base-button {
  @include drop-button-styles;

  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: rem(16px);
  will-change: opacity, scale;
  line-height: unset;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: rem(10px);
  padding: 0 rem(20px);
  max-width: var(--button-max-width, auto);

  @include bp-media('xs', 'sm') {
    padding: 0 rem(10px);
  }

  &:disabled {
    @include action-effect-button(var(--color-surface-placeholder-900));
  }

  & > p {
    font-size: rem(14px);
    font-weight: 700;
    width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__icon {
    align-items: center;
    height: 100%;
    display: flex;

    &--prepend {
      justify-content: flex-start;
    }

    &--append {
      justify-content: flex-end;
    }
  }

  &__circle {
    border: 1px solid var(--color-foreground-light);
    border-radius: 100%;
    position: absolute;
    opacity: 0.1;
  }

  &__circle--top-left {
    width: 100px;
    height: 100px;
    top: -65px;
    left: -40px;
  }

  &__circle--bottom-left {
    width: 50px;
    height: 50px;
    bottom: -20px;
    left: 15px;
  }

  &__circle--top-right {
    width: 100px;
    height: 100px;
    top: -65px;
    right: -35px;
  }

  &__circle--bottom-right {
    width: 70px;
    height: 70px;
    bottom: -30px;
    right: -30px;
  }

  &__loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &--with-full {
    width: 100%;
    gap: rem(15px);
  }

  &--with-decor {
    box-shadow: inset 0 0 3px 0 var(--color-surface-bg);
  }

  &--with-loading {
    p,
    .base-button__icon {
      visibility: hidden;
    }
  }

  &--navigated {
    gap: rem(6px);

    .base-button__circle {
      border: 1px solid var(--color-foreground-regular);
    }

    .base-button__icon--prepend {
      flex: 0;
    }

    > p {
      text-align: left;
      font-size: rem(16px);
      font-weight: 600;
      line-height: rem(19.5px);
      flex-grow: 0;
      display: flex;
      align-items: center;
      gap: rem(12px);
    }
  }

  &--size {
    &-mini {
      height: var(--button-height, 28px);
      min-width: var(--button-height, 28px);
      padding: 0 rem(8px);
      gap: rem(6px);

      > p {
        @include typo-text-mini;

        font-weight: 600;
      }
    }

    &-sm {
      height: var(--button-height, 38px);
      min-width: var(--button-height, 38px);

      > p {
        font-weight: 600;
      }
    }

    &-md {
      height: var(--button-height, 42px);
      min-width: var(--button-height, 42px);

      @include bp-media('xs', 'sm') {
        border-radius: var(--radius-xs);
        height: var(--button-height, 36px);
        min-width: var(--button-height, 36px);
      }
    }

    &-md-alt {
      height: var(--button-height, 40px);
      min-width: var(--button-height, 40px);
      padding: 0 rem(14px);
      gap: rem(5px);
    }
  }

  &--with-icon-dense {
    justify-content: center;

    p {
      width: auto;
    }
  }

  &--rounded {
    border-radius: 50px;
  }

  &--shadow {
    box-shadow: 0 0 12px 0 #3333;
  }

  &--variant {
    &-secondary {
      @include action-effect-button(var(--color-surface-placeholder));

      color: var(--color-foreground-regular);

      &.base-button--with-decor .base-button__circle {
        border: 1px solid var(--color-foreground-regular);
      }
    }

    &-dark {
      @include action-effect-button(var(--color-surface-inverted-900));

      color: var(--color-foreground-inverted);

      &.base-button--with-decor .base-button__circle {
        border: 1px solid var(--color-foreground-light);
      }

      &.base-button--flat {
        @include action-effect-button(var(--color-surface-placeholder));

        border: 2px solid var(--color-surface-placeholder);
        background-color: var(--color-surface-placeholder);
        color: var(--color-foreground-caption);
      }
    }

    &-light {
      @include action-effect-button(var(--color-surface-bg));

      color: var(--color-foreground-main);

      &.base-button--with-decor .base-button__circle {
        border: 1px solid var(--color-foreground-caption);
      }
    }

    &-primary {
      @include action-effect-button(var(--color-surface-primary));

      color: var(--color-foreground-light);

      &.base-button--outlined {
        border: 2px solid var(--color-foreground-primary);
        background-color: rgb(0 0 0 / 0%);
        color: var(--color-foreground-primary);

        > p {
          font-weight: 600;
        }
      }

      &.base-button--flat {
        @include action-effect-button(var(--color-surface-placeholder));

        border: 2px solid var(--color-surface-placeholder);
        background-color: var(--color-surface-placeholder);
        color: var(--color-foreground-primary);
      }
    }

    &-danger {
      @include action-effect-button(var(--color-surface-danger));

      color: var(--color-foreground-light);

      &.base-button--flat {
        @include action-effect-button(var(--color-surface-placeholder));

        border: 2px solid var(--color-surface-placeholder);
        background-color: var(--color-surface-placeholder);
        color: var(--color-foreground-danger);
      }
    }

    &-success {
      @include action-effect-button(var(--color-surface-success));

      color: var(--color-foreground-light);
    }

    &-transparent {
      @include action-effect-button(transparent);

      color: var(--color-foreground-inverted);
    }

    &-transparent-inverted-100 {
      @include action-effect-button(
          var(--color-surface-transparent-inverted-100)
      );
    }
  }

  &--with-mobile {
    @include bp-media('xs', 'sm') {
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      p {
        display: none;
      }

      .base-button__icon--prepend ~ .base-button__icon--append {
        display: none;
      }
    }
  }

  &--with-custom-color {
    @include action-effect-button(var(--button-custom-color));

    color: var(--color-foreground-light);
  }
}
</style>
